import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { privateListsApi } from "../../api/hliLists";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { Button, Container, EmptyState, InfoBox, Loading, Modal, PageHeader, RowAction, Table, Tabs, Textarea } from "../ui";
import { useYearOptions } from "../bursaries/shared";
import { ListHistory, ListStateTag, ListTotals, StudentsTable, listColumns } from "./ListParts";
import { BRD_LISTS_PATH, KIND_LABELS, PRIVATE_LISTS_KEY, STAFF_LISTS_PATH, STAFF_TABS, decisionsFor, studentsLabel, useBrdLists, useList, useStaffLists } from "./model";

const MAX_REASON = 1000;

// The lists private institutions sent: the HEC Admin, the DG and the Minister find what waits on them here
export function PrivateListsPage() {
  const [state, setState] = useState("");
  const query = useStaffLists(state);
  return (
    <ListsPage
      title="Institution lists"
      subtitle="Private institutions' lists of students for loans and bursaries. The HEC Admin forwards a list to the DG, the DG approves it to the Minister, and the Minister's approval sends it to BRD."
      basePath={STAFF_LISTS_PATH}
      query={query}
      tabs={<Tabs tabs={STAFF_TABS} value={state} onChange={setState} className="w-fit" />}
      exportName="private-institution-lists"
    />
  );
}

// The approved lists, for BRD to pay out
export function BrdPrivateListsPage() {
  return (
    <ListsPage
      title="Private institution lists"
      subtitle="Lists of students the Minister approved, for BRD to pay out"
      basePath={BRD_LISTS_PATH}
      query={useBrdLists()}
      exportName="approved-private-institution-lists"
    />
  );
}

function ListsPage({ title, subtitle, basePath, query, tabs, exportName }) {
  const navigate = useNavigate();
  const years = useYearOptions();
  const { data = [], isPending, isError, refetch } = query;
  const open = (list) => navigate(`${basePath}/${list.id}`);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title={title} subtitle={subtitle} />
      {tabs}
      {isError ? (
        <EmptyState title="Could not load the lists" onRefresh={refetch} />
      ) : (
        <Table cardView viewKey="private-lists"
          columns={[...listColumns(years, true), { key: "open", label: "", exportable: false, render: (l) => <RowAction Icon={EyeIcon} label="Open" onClick={() => open(l)} /> }]}
          data={data}
          rowKey={(l) => l.id}
          onRowClick={open}
          isLoading={isPending}
          defaultSort={{ key: "updated", direction: "desc" }}
          searchPlaceholder="Search by institution or list"
          exportName={exportName}
          emptyTitle="No lists here"
        />
      )}
    </Container>
  );
}

// One list with its students and history; whoever holds it decides on it as a whole
export function PrivateListPage({ basePath = STAFF_LISTS_PATH }) {
  const { listId } = useParams();
  const { data: list, isPending, isError, refetch } = useList(listId, false);
  const [deciding, setDeciding] = useState(null);

  if (isPending) return <Loading label="Loading the list…" />;
  if (isError || !list) return <EmptyState title="This list was not found" onRefresh={refetch} />;

  const decisions = decisionsFor(list.state);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={basePath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All lists
      </Link>
      <PageHeader
        title={`${list.institutionName} · ${list.title}`}
        subtitle={`${KIND_LABELS[list.kind]} · ${list.academicYearName ?? ""}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ListStateTag state={list.state} />
            {decisions.map((d) => (
              <Button key={d.action} size="sm" variant={d.destructive ? "danger-soft" : "solid"} onClick={() => setDeciding(d)}>
                {d.label}
              </Button>
            ))}
          </div>
        }
      />
      {list.state === "REJECTED" && list.reason && <InfoBox>Rejected: {list.reason}</InfoBox>}
      <ListTotals students={list.students} totalTuition={list.totalTuition} totalAllowance={list.totalAllowance} />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <StudentsTable entries={list.entries} exportName={`${list.institutionName}-${list.title}-students`} />
        <ListHistory history={list.history} />
      </div>
      {deciding && <DecisionDialog list={list} decision={deciding} onClose={() => setDeciding(null)} />}
    </Container>
  );
}

// A decision on the whole list; a rejection needs a reason, which the institution sees
function DecisionDialog({ list, decision, onClose }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);

  const decide = useMutation({
    mutationFn: () => privateListsApi.decide(list.id, { action: decision.action, ...(reason.trim() && { reason: reason.trim() }) }),
    onSuccess: () => {
      toast.success(`${decision.label}: ${list.title}`);
      queryClient.invalidateQueries({ queryKey: [PRIVATE_LISTS_KEY] });
      onClose();
    },
    onError: (err) => {
      console.error(`Failed to ${decision.action} list ${list.id}:`, err);
      // These codes mean something else on abroad loans, so their messages are set here
      const code = problemOf(err)?.code;
      setError(
        code === "not-your-turn"
          ? "This list is no longer with you. Reload to see where it is."
          : code === "reason-required"
            ? "Say why: the institution sees the reason."
            : errorMessageOf(err, "Failed to save the decision. Please try again.")
      );
    },
  });

  const submit = () => {
    setError(null);
    if (decision.reason && !reason.trim()) return setError("Say why: the institution sees the reason.");
    decide.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!decide.isPending}
      title={`${decision.label}: ${list.title}`}
      description={`${list.institutionName} · ${studentsLabel(list.students)}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button size="sm" variant={decision.destructive ? "danger" : "solid"} onClick={submit} loading={decide.isPending}>
            {decision.label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decision.reason ? (
          <Textarea label="Reason (the institution sees it)" required rows={3} maxLength={MAX_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />
        ) : (
          <p className="text-sm text-slate-600">The decision is on the whole list of {studentsLabel(list.students)}.</p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
